import React from 'react';

import {CriteriaStrip, Stripe} from 'src/components/criteria-strip/criteria-strip';
import {DataTypes} from 'src/components/data-types/data-types';
import {ColumnHeader} from 'src/components/filter-header/column-headers/column-header';
import {NoResultsRow} from 'src/components/no-results-row/no-results-row';
import {SortConfig} from 'src/hooks/useSortingRows';
import {COLUMN_CAPTIONS} from 'src/modules/attributes/constants';
import {createPredicates} from 'src/modules/attributes/filter-utils/createPredicates';
import {extractColumnHeaderData} from 'src/modules/attributes/filter-utils/extractColumnHeaderData';
import {Filters, SortColumns} from 'src/modules/attributes/typedefs';
import {AttributeRelationInfo} from 'src/openapi/models/AttributeRelationInfo';

import Group from '@pb/kit-component-group';
import Loading from '@pb/kit-component-loading';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';

import {ColumnTypes, ColumnWidth} from '../constants';

import {EventCodePillsStrip} from './event-code-pills-strip/event-code-pills-strip';

type Props = {
    preparedRows: Array<AttributeRelationInfo>,
    rows: Array<AttributeRelationInfo>,
    setFilter: (filterType: string, filterValue: Array<string>) => void,
    filters: Filters,
    onSort: (config: SortConfig<SortColumns>) => void,
    sortConfig: SortConfig<SortColumns>,
    criteriaStripeData: Array<Stripe>,
    clearAllFilters: () => void,
    intersectRef: (node: Element | null) => void,
    isVisible: boolean,
    hasMore: boolean,
};

const AMOUNT_LEFT_TO_UPLOAD_NEXT = 5;

export const AttributesView = (props: Props) => {
    const {
        preparedRows,
        rows,
        setFilter,
        filters,
        onSort,
        sortConfig,
        criteriaStripeData,
        clearAllFilters,
        intersectRef,
        isVisible,
        hasMore,
    } = props;

    return (
        <Group direction="v" gap="xxs">
            <CriteriaStrip data={criteriaStripeData} clearAllFilters={clearAllFilters}/>
            <Table>
                <Head>
                    <Cell width={ColumnWidth[ColumnTypes.ATTRIBUTE]}>
                        <ColumnHeader
                            filters={filters}
                            setFilter={setFilter}
                            filterPredicates={createPredicates}
                            rows={rows}
                            onSort={onSort}
                            sortConfig={sortConfig}
                            filteringColumnType={ColumnTypes.ATTRIBUTE}
                            sortingColumnType={ColumnTypes.ATTRIBUTE}
                            caption={COLUMN_CAPTIONS[ColumnTypes.ATTRIBUTE]}
                            columnDataHandler={extractColumnHeaderData}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.DATA_TYPE]}>
                        <ColumnHeader
                            filters={filters}
                            setFilter={setFilter}
                            filterPredicates={createPredicates}
                            rows={rows}
                            filteringColumnType={ColumnTypes.DATA_TYPE}
                            caption={COLUMN_CAPTIONS[ColumnTypes.DATA_TYPE]}
                            columnDataHandler={extractColumnHeaderData}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_CODES]}>
                        <ColumnHeader
                            filters={filters}
                            setFilter={setFilter}
                            filterPredicates={createPredicates}
                            rows={rows}
                            filteringColumnType={ColumnTypes.EVENT_CODES}
                            caption={COLUMN_CAPTIONS[ColumnTypes.EVENT_CODES]}
                            columnDataHandler={extractColumnHeaderData}
                        />
                    </Cell>
                </Head>
                <Body>
                    {
                        preparedRows.length ? (
                            preparedRows.map((row, i, arr) => (
                                <Row
                                    key={i}
                                    ref={node => (i === arr.length - AMOUNT_LEFT_TO_UPLOAD_NEXT ?
                                            intersectRef(node as Element) : undefined)}>
                                    <Cell title={true}>{row[ColumnTypes.ATTRIBUTE]}</Cell>
                                    <Cell>
                                        <DataTypes dataTypes={row[ColumnTypes.DATA_TYPE]}/>
                                    </Cell>
                                    <Cell>
                                        <EventCodePillsStrip
                                            eventCodes={row[ColumnTypes.EVENT_CODES]}
                                            attributeName={row[ColumnTypes.ATTRIBUTE]}
                                            attributeId={0}
                                        />
                                    </Cell>
                                </Row>
                            ))
                        ) : <NoResultsRow colSpan={3}/>
                    }
                </Body>
            </Table>
            {(isVisible && hasMore) ? <Loading fast={true}/> : null}
        </Group>
    );
};
